import { stripTrailingDecimalZeros } from "@Light/utils/formatting";
import { formatTimeAsAmPm } from "@Light/utils/planFeatures";
import type {
  EVChargingPricingTierSchema,
  EVChargingScheduleSchema,
} from "@Light/services/lightTypes";

export function formatChargingRates(
  rates: EVChargingPricingTierSchema[],
  {
    capitalize = false,
    literalZero = false,
  }: { capitalize?: boolean; literalZero?: boolean } = {},
): string {
  const sorted = [...rates].sort((a, b) => a.tier - b.tier);
  const parts = sorted.map((r) => {
    let rateStr =
      parseFloat(r.rate) === 0 && !literalZero
        ? "free charging"
        : `${stripTrailingDecimalZeros(r.rate)}¢ per kWh`;
    if (r.cap !== null && r.cap !== undefined) {
      rateStr += ` - Up to ${stripTrailingDecimalZeros(r.cap)} kWh monthly`;
    } else if (rates.length === 1 && !literalZero) {
      rateStr = "unlimited " + rateStr;
    }
    return rateStr;
  });
  const result = parts.join(" then ");
  if (capitalize && result.length > 0) {
    return result.charAt(0).toUpperCase() + result.slice(1);
  }
  return result;
}

/**
 * The first on-schedule tier's rate on its own (no cap or "unlimited" wording),
 * for compact labels like "(5¢ per kWh)". Undefined when no tiers are defined.
 */
export function formatOnScheduleRate(
  rates: EVChargingPricingTierSchema[],
): string | undefined {
  const [firstTier] = [...rates].sort((a, b) => a.tier - b.tier);
  if (!firstTier) {
    return undefined;
  }
  return formatChargingRates([{ ...firstTier, cap: null }], {
    literalZero: true,
  });
}

export function formatChargingSchedule(
  schedules: EVChargingScheduleSchema[],
): string {
  return schedules
    .map(
      (s) =>
        `${formatTimeAsAmPm(s.start_time)}-${formatTimeAsAmPm(s.end_time)}`,
    )
    .join(" and ");
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatBaseFee(baseFee: number): string {
  return currencyFormatter.format(baseFee);
}
